import React from "react";
import { TemplateItem } from "../utils/pageGroupPayload";
import { TemplateEditorProps, templateImageUrl } from "../utils/templatePageForm";
import { requiredMark, requireLabelStyles } from "./BaseModal";
import { Box, ColorInput, Divider, Group, Select, Stack, Text, Textarea, TextInput } from "@mantine/core";
import ImageDropzoneField from "./ImageDropzoneField";

interface Props extends TemplateEditorProps {
  templates: TemplateItem[] | undefined
}

/** 必須マーク付きのラベル */
const requiredLabel = (text: string) => (
  <>
    <span>{text}</span>
    {requiredMark}
  </>
)

const CommonTemplateEditor: React.FC<Props> = ({
  form,
  images,
  templates,
  onUploadImage,
  onClearImage,
}) => {
  const templateType = form.values.template_type

  // page_type 0（共通設定用）のテンプレートを並び順で表示する
  // 'original' は表示名だけ 'simple' に読み替える
  const templateOptions = (templates ?? [])
    .filter((row) => row.page_type === 0)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((row) => ({
      value: String(row.id),
      label: row.name === 'original' ? 'simple' : row.name,
    }))

  const sentenceRow = (
    before: string,
    field:
      | 'incentive_action'
      | 'incentive_content'
      | 'incentive_amount'
      | 'incentive_content_guest'
      | 'incentive_amount_guest',
    after: string,
    options: { required?: boolean; placeholder?: string } = {},
  ) => (
    <Group align="flex-start" gap={8} wrap="nowrap">
      {before && (
        <Text size="sm" mt={6} style={{ flexShrink: 0 }}>
          {before}
        </Text>
      )}
      <TextInput
        style={{ flex: 1 }}
        placeholder={options.placeholder}
        {...form.getInputProps(field)}
      />
      <Text size="sm" mt={6} style={{ flexShrink: 0 }}>
        {after}
      </Text>
      <Box
        mt={4}
        style={{ flexShrink: 0, visibility: options.required ? 'visible' : 'hidden' }}
      >
        {requiredMark}
      </Box>
    </Group>
  )

  return (
    <Stack gap="lg">
      <Select
        label={requiredLabel('デザインテンプレートの種類を選んでください')}
        styles={requireLabelStyles}
        data={templateOptions}
        allowDeselect={false}
        {...form.getInputProps('template_type')}
        value={templateType ? String(templateType) : null}
        onChange={(v) => form.setFieldValue('template_type', v ? Number(v) : 0)}
      />

      <Box>
        <Text size="sm" fw={500} mb={8}>
          ページカラーを選択する
        </Text>
        <Group grow align="flex-start" gap={16}>
          <ColorInput
            label="テーマカラーを選択します。"
            format="hex"
            {...form.getInputProps('theme_color')}
          />
          <ColorInput
            label="ボタンカラーを選択します。"
            format="hex"
            {...form.getInputProps('button_color')}
          />
        </Group>
      </Box>

      <TextInput
        label={requiredLabel('紹介からの目標コンバージョン数を設定してください')}
        styles={requireLabelStyles}
        rightSection={<Text size="sm" c="#00000099">件／月</Text>}
        rightSectionWidth={64}
        rightSectionPointerEvents="none"
        {...form.getInputProps('target_cv_num')}
      />

      <TextInput
        label="キャンペーン管理用の概要を設定してください"
        {...form.getInputProps('pageDescription')}
      />

      <ImageDropzoneField
        label="サービスロゴとして表示する画像ファイルを設定してください。"
        description="※高さ64ピクセルに合わせて中央に表示されます。64×64ピクセル以上のjpgまたはpng画像を選択してください"
        required
        value={form.values.logo}
        uploaded={images.LOGO}
        defaultUrl={templateImageUrl(templateType, 'logo.png')}
        onDrop={(files) => onUploadImage('LOGO', files)}
        onClear={() => onClearImage('LOGO')}
        error={form.errors.logo}
        dataPath="logo"
      />

      <Box>
        <Text size="sm" fw={500} mb={12}>
          特典付与のタイミングと特典の内容をそれぞれ30文字以内で設定
        </Text>

        {sentenceRow('友人（guest）が', 'incentive_action', 'すると、', { required: true })}

        <Divider my={16} />

        {sentenceRow('紹介者（inviter）に', 'incentive_content', 'を', { required: true })}

        <Box mt={12}>
          {sentenceRow('', 'incentive_amount', 'プレゼント', { placeholder: '例：500円分（金額）' })}
        </Box>
      </Box>

      <ImageDropzoneField
        label="紹介者（inviter）の特典画像を変更"
        description="※200×200ピクセル以上の正方形のjpgあるいはpng画像を選択してください。"
        value={form.values.incentive_image}
        uploaded={images.INCENTIVE_IMAGE}
        defaultUrl={templateImageUrl(templateType, 'img_box_1_icon.png')}
        onDrop={(files) => onUploadImage('INCENTIVE_IMAGE', files)}
        onClear={() => onClearImage('INCENTIVE_IMAGE')}
        error={form.errors.incentive_image}
        dataPath="incentive_image"
      />

      <Box>
        {sentenceRow('友人（guest）に', 'incentive_content_guest', 'を', { required: true })}

        <Box mt={12}>
          {sentenceRow('', 'incentive_amount_guest', 'プレゼント', { placeholder: '例：500円分（金額）' })}
        </Box>
      </Box>

      <ImageDropzoneField
        label="友人（guest）の特典画像を変更"
        description="※200×200ピクセル以上の正方形のjpgあるいはpng画像を選択してください。"
        value={form.values.incentive_image_guest}
        uploaded={images.INCENTIVE_IMAGE_GUEST}
        defaultUrl={templateImageUrl(templateType, 'img_box_1_icon.png')}
        onDrop={(files) => onUploadImage('INCENTIVE_IMAGE_GUEST', files)}
        onClear={() => onClearImage('INCENTIVE_IMAGE_GUEST')}
        error={form.errors.incentive_image_guest}
        dataPath="incentive_image_guest"
      />

      <Textarea
        label={requiredLabel('キャンペーンに関する注意事項を500文字以内で指定してください。')}
        styles={requireLabelStyles}
        rows={5}
        {...form.getInputProps('notice')}
      />

      <TextInput
        label="利用規約が記載されたURLを設定してください。"
        description="※URLを指定しない場合、利用規約のボタンは表示されません。"
        placeholder="https://www.website.com/"
        {...form.getInputProps('terms')}
      />

      <TextInput
        label={requiredLabel('個人情報保護方針が記載されたURLを設定してください。')}
        styles={requireLabelStyles}
        description="※個人情報を取得するため、必ずご準備をお願いいたします。"
        placeholder="https://www.website.com/"
        {...form.getInputProps('privacy_policy')}
      />

      <Textarea
        label={requiredLabel('LINEで送信されるメッセージを入力します。')}
        styles={requireLabelStyles}
        rows={5}
        {...form.getInputProps('line_message')}
      />
    </Stack>
  )
}

export default CommonTemplateEditor
